/* =========================================================
   Paragraph PDP — production JS
   Two custom elements:
     <pack-selector>  — pack quantity card UI, dispatches pack:change
     <paragraph-atc>  — ATC button label + sticky mobile bar
   ~180 LOC vanilla JS, no build step, no npm deps.
   ========================================================= */

/* ---------- i18n helpers ---------- */
// Localized plural for bottle count. Falls back to English if
// window.paragraphStrings isn't wired (dev mode, early load, etc.).
function _tBottleCount(qty) {
  const strings = (window.paragraphStrings && window.paragraphStrings.bottleCount) || null;
  const template = strings
    ? (qty === 1 ? strings.one : strings.other)
    : (qty === 1 ? '[count] bottle' : '[count] bottles');
  return template.replace('[count]', qty);
}

function _tSavePercent(pct) {
  const template = (window.paragraphStrings && window.paragraphStrings.savePercent) || 'Save [percent]%';
  return template.replace('[percent]', pct);
}

function _tAddToCart() {
  return (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
}

function _tUnitSuffix() {
  return (window.paragraphStrings && window.paragraphStrings.unitSuffix) || '/ btl';
}

/* ---------- PackSelector custom element ---------- */
class PackSelector extends HTMLElement {
  connectedCallback() {
    this._options = Array.from(this.querySelectorAll('.pack-option'));
    if (!this._options.length) return;

    // Ensure radiogroup semantics
    this.setAttribute('role', 'radiogroup');

    this._options.forEach((btn, i) => {
      // Only first is initially tabbable
      btn.tabIndex = i === 0 ? 0 : -1;

      btn.addEventListener('click', () => this._select(i));
      btn.addEventListener('keydown', (e) => this._onKey(e, i));
    });

    // Dispatch initial state so ATC can read it on connect
    this._dispatchChange();
  }

  _select(idx) {
    const last = this._options.length - 1;
    this._options.forEach((btn, i) => {
      const sel = i === idx;
      btn.classList.toggle('is-selected', sel);
      btn.setAttribute('aria-checked', sel ? 'true' : 'false');
      btn.tabIndex = sel ? 0 : -1;
    });
    this._options[idx].focus();
    this._dispatchChange();
  }

  _onKey(e, i) {
    const last = this._options.length - 1; // eslint-disable-line no-unused-vars
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown')  { e.preventDefault(); next = i === last ? 0 : i + 1; }
    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')    { e.preventDefault(); next = i === 0    ? last : i - 1; }
    if (e.key === 'Home')                                 { e.preventDefault(); next = 0; }
    if (e.key === 'End')                                  { e.preventDefault(); next = last; }
    if (next !== null) this._select(next);
  }

  _dispatchChange() {
    const selected = this._options.find((b) => b.classList.contains('is-selected')) || this._options[0];
    if (!selected) return;

    const qty      = Number(selected.dataset.packQty      || 1);
    const discount = Number(selected.dataset.packDiscount || 0);
    const label    = selected.dataset.packLabel || '';
    const unitPrice = Number(this.dataset.productPrice    || 0) / 100; // Shopify price in cents
    const unitAfterDiscount = unitPrice * (1 - discount / 100);

    // Update the mobile summary line (HALF CASE · 6 BOTTLES — SAVE 5%)
    const summaryName = this.querySelector('[data-pack-summary-name]');
    const summarySave = this.querySelector('[data-pack-summary-save]');
    if (summaryName) {
      summaryName.textContent = label + ' · ' + _tBottleCount(qty);
    }
    if (summarySave) {
      if (discount > 0) {
        summarySave.textContent = _tSavePercent(discount);
        summarySave.hidden = false;
      } else {
        summarySave.textContent = '';
        summarySave.hidden = true;
      }
    }

    // Sync the Shopify form quantity field
    const formId  = this.dataset.formId;
    const form    = formId ? document.getElementById(formId) : null;
    if (form) {
      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type  = 'hidden';
        qtyInput.name  = 'quantity';
        form.appendChild(qtyInput);
      }
      qtyInput.value = qty;
    }

    this.dispatchEvent(new CustomEvent('pack:change', {
      bubbles: true,
      detail: { qty, discount, unitPrice, unitAfterDiscount, totalPrice: unitAfterDiscount * qty }
    }));
  }

  // Called by ParagraphAtc when a variant:change fires (price update)
  updatePrice(newUnitPriceCents) {
    this.dataset.productPrice = newUnitPriceCents;
    this._dispatchChange();
  }
}
if (!customElements.get('pack-selector')) customElements.define('pack-selector', PackSelector);


/* ---------- ParagraphAtc custom element ---------- */
class ParagraphAtc extends HTMLElement {
  connectedCallback() {
    this._submitBtn = document.getElementById(this.dataset.submitButtonId);
    // The navy .pdp-sticky-atc bar by class. A second bar that shared the
    // data-sticky-atc hook (pw-mobile-sticky-atc) made each script drive
    // the other's bar, so neither ever showed on phones; it was removed in
    // Sept 2026 (Meta in-app browser audit).
    this._stickyAtc = document.querySelector('.pdp-sticky-atc');
    this._stickyPrice = document.querySelector('[data-sticky-price]');
    // Sold-out products never show the bar. The CSS rule for this
    // (product-info[data-pw-sold-out] .pdp-sticky-atc) stops matching once
    // the bar is moved to <body> below, so the state is read here.
    const info = this.closest('product-info');
    this._soldOut = !!(info && info.hasAttribute('data-pw-sold-out'));

    // Move the sticky ATC bar to <body> so `position: fixed` is relative
    // to the viewport — escaping any transformed ancestors (Shopify's
    // sticky-header, scroll-trigger animation wrappers, etc.) which
    // would otherwise create a containing block and clip it.
    if (this._stickyAtc && this._stickyAtc.parentElement !== document.body) {
      document.body.appendChild(this._stickyAtc);
    }

    // Listen for pack changes
    document.addEventListener('pack:change', (e) => this._onPackChange(e.detail));

    // Trigger an initial render by re-dispatching from the existing pack selector.
    // This handles the race where pack-selector connected before ATC did.
    const existingSelector = document.querySelector('pack-selector');
    if (existingSelector && typeof existingSelector._dispatchChange === 'function') {
      // Defer one tick so the event listener above is fully attached
      requestAnimationFrame(() => existingSelector._dispatchChange());
    }

    // Listen for Dawn variant changes (price update)
    document.addEventListener('variant:change', (e) => {
      const selector = this.closest('[data-section]')?.querySelector('pack-selector');
      if (selector && e.detail?.variant?.price != null) {
        selector.updatePrice(e.detail.variant.price);
      }
    });

    // Sticky reveal via IntersectionObserver on the in-flow submit button:
    // shown whenever the button is off screen, below the fold on load (it
    // sits at ~800 px on a 717 px phone screen) as well as after scrolling
    // past it. Hidden again when the footer comes into view (don't overlap
    // the footer).
    this._submitOffscreen = false;
    this._footerInView = false;
    if (this._submitBtn && this._stickyAtc && 'IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this._submitOffscreen = !entry.isIntersecting;
            this._updateStickyVisibility();
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );
      this._io.observe(this._submitBtn);

      // Also watch the footer — hide sticky bar when footer is on screen.
      // Footer section group only: a looser [class*="footer"] matched the
      // cart drawer's footer first whenever the cart had items.
      const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer.footer');
      if (footer) {
        this._footerIo = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._footerInView = entry.isIntersecting;
              this._updateStickyVisibility();
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this._footerIo.observe(footer);
      }
    }
  }

  disconnectedCallback() {
    if (this._io && this._submitBtn) this._io.unobserve(this._submitBtn);
    if (this._footerIo) this._footerIo.disconnect();
  }

  _updateStickyVisibility() {
    this._setStickyVisible(!this._soldOut && this._submitOffscreen && !this._footerInView);
  }

  _onPackChange({ unitAfterDiscount, totalPrice }) {
    if (!this._submitBtn) return;

    const currency = this.dataset.currencySymbol || '$';
    // Format helper: strip trailing .00, keep .XX for non-whole dollars.
    const fmt = (n) => {
      const two = n.toFixed(2);
      return currency + (two.endsWith('.00') ? two.slice(0, -3) : two);
    };
    const unitFmt  = fmt(unitAfterDiscount);
    const totalFmt = fmt(totalPrice);

    // Dawn's submit button wraps the label in `<span>` (first child) +
    // a loading-spinner <svg> sibling. Target the label span only so we
    // don't blow away the spinner.
    const btn = this._submitBtn;
    const span = btn.querySelector('span:not(.svg-wrapper)') || btn.querySelector('span');
    if (span) {
      span.innerHTML =
        '<span class="atc-cta">' + _tAddToCart() + '</span>' +
        '<span class="atc-totals">' +
          '<span class="atc-total">' + totalFmt + '</span>' +
          '<span class="atc-unit">' + unitFmt + ' ' + _tUnitSuffix() + '</span>' +
        '</span>' +
        '<span class="atc-arrow" aria-hidden="true">→</span>';
    }
    btn.classList.add('product-form__submit--compound');

    // Update sticky. The title stays the server-rendered product title; it
    // used to be replaced with document.title, which is the SEO title
    // ("Paragraph Non-Alcoholic Tempranillo ...") and truncated in the bar.
    if (this._stickyPrice) this._stickyPrice.textContent = totalFmt;
  }

  _setStickyVisible(visible) {
    if (!this._stickyAtc) return;
    this._stickyAtc.classList.toggle('is-visible', visible);
    this._stickyAtc.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
}
if (!customElements.get('paragraph-atc')) customElements.define('paragraph-atc', ParagraphAtc);


/* ---------- Band motif: fill #band-grid or .paragraph-brand-band__motif ---------- */
(function buildBandMotif() {
  const container = document.querySelector('.paragraph-brand-band__motif');
  if (!container) return;

  const COLS = 30;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ ';
  let text = '';
  for (let i = 0; i < COLS * 14; i++) {
    text += alphabet[Math.floor(Math.random() * alphabet.length)];
    if ((i + 1) % COLS === 0) text += '\n';
  }
  container.textContent = text;
})();
